/**
 * BOUSS — moteur de cross-sell (mécanisme Hoshi).
 *
 * Liquid ne connaît pas le panier au rendu (caches CDN/section) : chaque
 * emplacement (encart fiche, tiroir panier, grille bas de fiche) rend donc un
 * POOL de candidats masqués ([data-bouss-xsell] > [data-xsell-item]). Ce
 * script lit le panier en GET /cart.js — seule requête autorisée, jamais
 * d'écriture — puis filtre de façon synchrone :
 *   - le produit consulté n'apparaît jamais dans son propre cross-sell ;
 *   - les produits déjà au panier sont retirés ;
 *   - un pack au panier retire aussi ses « atomes » (bouss.bundle_contains,
 *     carte JSON [data-bouss-atoms]) — le panier ne splitte jamais un pack ;
 *   - sous data-min survivants, le bloc entier se masque.
 *
 * Après un ajout, Horizon re-rend la section header (cart:update + morph) :
 * les blocs reviennent du serveur non filtrés (sans data-xsell-done). Le
 * MutationObserver ci-dessous le détecte et re-filtre — c'est le mécanisme
 * porteur, l'écoute de cart:update n'est qu'un raccourci.
 */

const BLOCK_SELECTOR = '[data-bouss-xsell]';

// ---- Titre produit (h1 de la fiche) sur une seule ligne ------------------
// Pas d'équivalent CSS de « réduire la police jusqu'à tenir » : on estime la
// taille cible proportionnellement au débordement puis on affine par
// demi-pixels (plancher 14px, l'ellipse CSS prend le relais en dessous). Le
// ResizeObserver refait le calcul quand l'élément change de largeur (resize)
// et la police regrandit si la place revient.
const PDP_TITLE_SELECTOR = '.product-information h1';
const PDP_TITLE_MIN_PX = 14;

const titleObserver = new ResizeObserver((entries) => {
  for (const entry of entries) fitTitle(entry.target);
});

function fitTitle(el) {
  if (!el.clientWidth) return; // pas encore affiché
  if (!el.dataset.boussTitleBase) {
    el.dataset.boussTitleBase = parseFloat(getComputedStyle(el).fontSize) || 12;
  }
  const base = parseFloat(el.dataset.boussTitleBase);
  const min = parseFloat(el.dataset.boussTitleMin) || PDP_TITLE_MIN_PX;
  el.style.fontSize = '';
  if (el.scrollWidth <= el.clientWidth + 1) return;
  let size = Math.max(min, Math.floor(((base * el.clientWidth) / el.scrollWidth) * 2) / 2);
  el.style.fontSize = `${size}px`;
  let guard = 12;
  while (el.scrollWidth > el.clientWidth + 1 && size > min && guard-- > 0) {
    size -= 0.5;
    el.style.fontSize = `${size}px`;
  }
}

function fitPdpTitle() {
  for (const el of document.querySelectorAll(PDP_TITLE_SELECTOR)) {
    fitTitle(el);
    titleObserver.observe(el);
  }
}

// ---- Centrage viewport de la colonne infos (desktop) ---------------------
// Le CSS cale la colonne sticky à (50dvh - hauteur/2) ; la hauteur est
// fournie ici (--bouss-details-h), remise à jour à chaque redimensionnement
// (accordéon ouvert, variante changée…).
const detailsObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    entry.target.style.setProperty('--bouss-details-h', `${entry.target.offsetHeight}px`);
  }
});

function observePdpDetails() {
  for (const el of document.querySelectorAll('.product-information .product-details')) {
    detailsObserver.observe(el);
  }
}

let atomData = null;

function readAtomData() {
  const node = document.querySelector('script[data-bouss-atoms]');
  if (!node) return { current: 0, products: {} };
  try {
    const parsed = JSON.parse(node.textContent);
    return { current: parsed.current || 0, products: parsed.products || {} };
  } catch {
    return { current: 0, products: {} };
  }
}

function filterBlock(block, coveredIds, currentId) {
  const max = parseInt(block.dataset.max, 10) || 2;
  const min = parseInt(block.dataset.min, 10) || 1;
  let shown = 0;

  for (const item of block.querySelectorAll('[data-xsell-item]')) {
    const productId = Number(item.dataset.productId);
    const atoms = (item.dataset.atoms || '')
      .split(',')
      .filter(Boolean)
      .map(Number);
    const excluded =
      productId === currentId ||
      coveredIds.has(productId) ||
      atoms.some((atom) => coveredIds.has(atom));
    const visible = !excluded && shown < max;
    item.hidden = !visible;
    if (visible) shown += 1;
  }

  block.hidden = shown < min;
  block.setAttribute('data-xsell-done', '');
}

async function refresh() {
  fitPdpTitle();
  observePdpDetails();

  const blocks = document.querySelectorAll(BLOCK_SELECTOR);
  // Le chip PANIER se met à jour même sans bloc cross-sell : panier vidé, le
  // tiroir re-rendu n'a plus de bloc et l'ancien nombre restait affiché
  // jusqu'au rechargement de la page.
  const counters = document.querySelectorAll('[data-bouss-cart-count]');
  if (!blocks.length && !counters.length) return;

  let cart;
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    cart = await response.json();
  } catch {
    return; // réseau indisponible : les blocs restent masqués, le panier fonctionne
  }

  // Compteur du chip PANIER (PDP mobile).
  for (const counter of counters) {
    counter.textContent = cart.item_count;
    counter.hidden = !cart.item_count;
  }

  if (!blocks.length) {
    window.__boussCartCount = cart.item_count;
    return;
  }
  atomData = readAtomData();

  // Ouverture du tiroir panier après un AJOUT. On n'ouvre QUE si le nombre
  // d'articles a AUGMENTÉ depuis le dernier état connu — ainsi une mise à
  // jour de quantité, un retrait ou le simple chargement de page n'ouvrent
  // pas le tiroir. Vaut pour l'ATC principal ET le cross-sell (les deux
  // passent par <product-form-component> → cart:update → ce refresh).
  if (window.__boussCartCount != null && cart.item_count > window.__boussCartCount) {
    const comp = document.querySelector('cart-drawer-component');
    const dlg = comp && comp.querySelector('dialog.cart-drawer__dialog');
    if (comp && typeof comp.open === 'function' && !(dlg && dlg.open)) {
      try { comp.open(); } catch (e) {}
    }
  }
  window.__boussCartCount = cart.item_count;

  // Carte des produits « couverts » : lignes du panier + atomes des packs.
  const covered = new Set();
  for (const line of cart.items || []) {
    covered.add(line.product_id);
    for (const atom of atomData.products[line.product_id] || []) covered.add(atom);
  }

  for (const block of blocks) filterBlock(block, covered, atomData.current);
}

// Re-filtrage paresseux : un seul refresh par frame, déclenché quand un bloc
// non filtré (re-rendu serveur après morph) apparaît dans le DOM.
let scheduled = false;
function scheduleRefresh() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    refresh();
  });
}

// Accordéons BOUSS (fiche) : ils s'ouvrent et se ferment sur place, sans
// défilement automatique. Le recalage en haut de l'écran (scrollIntoView
// répété, déclenché par `toggle` et par le clic) faisait sauter la page à
// chaque toucher.

// Croix du tiroir panier : la fermeture native (délégation on:click du thème
// puis closeDialog, qui attend la fin des animations du dialog) peut rester
// suspendue sur la vraie page. Si le dialog est toujours ouvert peu après le
// clic, fermeture forcée + libération du verrou de défilement posé par
// showDialog (body en position:fixed, scroll mémorisé dans top).
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('.cart-drawer__close-button');
    if (!button) return;
    const dialog = button.closest('dialog');
    if (!dialog) return;
    setTimeout(() => {
      if (!dialog.open) return;
      // Filet de sécurité : si la fermeture native reste suspendue (l'animation
      // n'émet jamais sa fin), on force juste la fermeture du dialog. On NE
      // touche PLUS au défilement ni aux styles du body : dialog.js (closeDialog)
      // libère le verrou et restaure la position DÈS le début de la fermeture,
      // avant ce délai. Y retoucher ici lisait un body.style.top déjà vidé (→ 0)
      // et renvoyait la page en haut (flash du hero, filtre nav décalé).
      dialog.classList.remove('dialog-closing');
      dialog.close();
    }, 600);
  },
  true
);

// Feedback du bouton cross-sell pendant l'ajout : le bouton natif du thème
// n'a pas de spinner dans nos lignes condensées. À la soumission du
// formulaire produit d'une ligne, on met le bouton en état « chargement »
// (spinner). En cas de succès la ligne disparaît (produit désormais au
// panier) ; en cas d'échec, on rétablit le bouton après un court délai.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('.bouss-hx-row__pf')) return;
    const btn = form.querySelector('.bouss-hx-row__btn');
    if (!btn || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    setTimeout(() => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }, 4000);
  },
  true
);

refresh();
document.addEventListener('cart:update', scheduleRefresh);
document.addEventListener('shopify:section:load', scheduleRefresh);
new MutationObserver(() => {
  // Bloc re-rendu par un morph, ou h1 remplacé (changement de variante).
  if (
    document.querySelector(`${BLOCK_SELECTOR}:not([data-xsell-done])`) ||
    document.querySelector(`${PDP_TITLE_SELECTOR}:not([data-bouss-title-base])`)
  ) {
    scheduleRefresh();
  }
}).observe(document.body, { childList: true, subtree: true });
